import { Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { SearchIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { CompanyPolicyStatus } from "@/modules/hr/company-policy/domain/types";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { usePolicyTypesQuery } from "../api/queries";

export type PolicySearchParams = {
  offset?: number;
  limit?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  policyTypeId?: string;
  status?: CompanyPolicyStatus;
  search?: string;
};

const VALUE_ALL = "__all__";
const POLICY_TYPE_ALL = "__all__";

const STATUS_OPTIONS = [
  { value: VALUE_ALL, label: "ທັງໝົດ" },
  { value: "draft", label: "ຮ່າງ" },
  { value: "active", label: "ໃຊ້ງານ" },
  { value: "archived", label: "ເກັບໄວ້" },
];

type PolicyFilterProps = {
  searchParams: PolicySearchParams;
  fromRoute: "/app/hr/company-policy" | "/app/hr/company-policy/archive";
  /** When true, hide status filter (e.g. Archive page always shows archived) */
  hideStatusFilter?: boolean;
};

export function PolicyFilter({
  searchParams,
  fromRoute,
  hideStatusFilter = false,
}: PolicyFilterProps) {
  const nav = useNavigate({ from: fromRoute });
  const search = searchParams;
  const { data: policyTypes = [] } = usePolicyTypesQuery();
  const policyTypeOptions = useMemo(
    () => [
      { value: POLICY_TYPE_ALL, label: "ທັງໝົດ" },
      ...policyTypes.map((t) => ({ value: t.id, label: t.name })),
    ],
    [policyTypes],
  );

  const [searchInput, setSearchInput] = useState(search.search ?? "");
  const [statusInput, setStatusInput] = useState<string>(
    search.status ?? VALUE_ALL,
  );
  const [policyTypeInput, setPolicyTypeInput] = useState(
    search.policyTypeId ?? POLICY_TYPE_ALL,
  );

  useEffect(() => {
    setSearchInput(search.search ?? "");
    setStatusInput(search.status ?? VALUE_ALL);
    setPolicyTypeInput(search.policyTypeId ?? POLICY_TYPE_ALL);
  }, [search.search, search.status, search.policyTypeId]);

  const limit = search.limit ?? 20;

  const apply = (opts: {
    policyTypeVal?: string;
    statusVal?: string;
    searchVal?: string;
  }) => {
    const pt = opts.policyTypeVal ?? policyTypeInput;
    const st = opts.statusVal ?? statusInput;
    const sv = opts.searchVal ?? searchInput;
    const policyTypeId = pt === POLICY_TYPE_ALL || !pt ? undefined : pt;
    const status = hideStatusFilter
      ? undefined
      : st === VALUE_ALL
        ? undefined
        : (st as CompanyPolicyStatus);
    const searchTerm = sv.trim() || undefined;

    if (fromRoute === "/app/hr/company-policy") {
      nav({
        search: {
          offset: 0,
          limit,
          sort: search.sort,
          policyTypeId,
          status,
          search: searchTerm,
        },
      });
    } else {
      nav({
        search: {
          offset: 0,
          limit,
          sort: search.sort,
          policyTypeId,
          search: searchTerm,
        },
      });
    }
  };

  const applySearch = (searchVal: string) => apply({ searchVal });
  const debouncedApplySearch = useDebounceCallback(applySearch, 400);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchInput(val);
    debouncedApplySearch(val);
  };

  const handlePolicyTypeChange = (value: string) => {
    setPolicyTypeInput(value);
    apply({ policyTypeVal: value });
  };

  const handleStatusChange = (value: string) => {
    setStatusInput(value);
    apply({ statusVal: value });
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <SearchIcon className="absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="policy-search"
            aria-label="ຄົ້ນຫາຕາມຊື່ ຫຼື ລະຫັດ"
            placeholder="ຄົ້ນຫານະໂຍບາຍ..."
            className="h-8 pl-8 sm:max-w-2xs"
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={(e) => e.key === "Enter" && applySearch(searchInput)}
          />
        </div>
        <SimpleSelect
          options={policyTypeOptions}
          value={policyTypeInput}
          onValueChange={handlePolicyTypeChange}
          placeholder="ປະເພດ"
          className="h-8 w-full sm:w-40"
          aria-label="ປະເພດ"
        />
        {!hideStatusFilter && (
          <SimpleSelect
            options={STATUS_OPTIONS}
            value={statusInput}
            onValueChange={handleStatusChange}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-32"
            aria-label="ສະຖານະ"
          />
        )}
      </div>
    </div>
  );
}
